import React, { useEffect, useMemo, useState } from "react";
import { useFontStyle } from "@/context/StyleContext";
import { FontFamily, FontStyleSettings, fontFamilies } from "@/lib/types";
import { Button, Select, InputNumber, Form, Tooltip, message } from "antd";
import Logger from "@/utility/logger";
import ColorSwatchPicker from "@/components/settings/ColorSwatchPicker";
import { SettingsRow } from "@/components/settings/SettingsLayout";

type Style = { name: string; styles: FontStyleSettings; setStyles: (styles: FontStyleSettings) => Promise<void> };

const FONT_COLOR_PRESETS = [
  "#141414",
  "#374151",
  "#1F6B3E",
  "#0069A5",
  "#2C5AA5",
  "#6B3FA0",
  "#B45309",
  "#9F1D1D",
  "#FFFFFF",
];

// Word's standard highlight palette.
const HIGHLIGHT_COLOR_PRESETS = [
  "#FFFF54",
  "#75FB4C",
  "#75FBFD",
  "#EA33F7",
  "#0000F5",
  "#EA3323",
  "#00007B",
  "#377E7F",
  "#377E22",
  "#75147C",
  "#75140C",
  "#808026",
  "#808080",
  "#C0C0C0",
  "#000000",
];

function UserStyleSettings({
  logAuditAction,
  reportTemplateProjectId,
}: {
  logAuditAction: (id: string, action: "CREATE" | "UPDATE" | "DELETE", section_name?: string) => Promise<void>;
  reportTemplateProjectId: string | null;
}) {
  const { userStyles, setUserStyles, draftStyles, setDraftStyles, confirmedStyles, setConfirmedStyles } =
    useFontStyle();

  const [selectedStyleName, setSelectedStyleName] = useState<string>(() => {
    try {
      return localStorage.getItem("jenly_selected_style_name") || "AI Generated";
    } catch {
      return "AI Generated";
    }
  });
  const [savedStyleName, setSavedStyleName] = useState<string>(() => {
    try {
      return localStorage.getItem("jenly_selected_style_name") || "AI Generated";
    } catch {
      return "AI Generated";
    }
  });
  const [isSaving, setIsSaving] = useState(false);

  const styles: Style[] = useMemo(
    () => [
      { name: "AI Generated", styles: draftStyles, setStyles: setDraftStyles },
      { name: "Finalized", styles: confirmedStyles, setStyles: setConfirmedStyles },
      { name: "Human Authored", styles: userStyles, setStyles: setUserStyles },
    ],
    [draftStyles, confirmedStyles, userStyles, setDraftStyles, setConfirmedStyles, setUserStyles]
  );

  const selectedStyles = useMemo(
    () => styles.find((style) => style.name === selectedStyleName) || styles[0],
    [styles, selectedStyleName]
  );

  const [form] = Form.useForm();

  useEffect(() => {
    if (selectedStyles?.styles) {
      try {
        form.setFieldsValue({
          fontFamily: selectedStyles.styles.fontFamily || "Arial",
          fontSize: selectedStyles.styles.fontSize || 10,
          fontColor: selectedStyles.styles.fontColor || "#000000",
          bold: !!selectedStyles.styles.bold,
          italic: !!selectedStyles.styles.italic,
          highlightColor: selectedStyles.styles.highlightColor || null,
        });
      } catch (e) {
        // ignore if form not mounted yet
      }
    }
  }, [selectedStyles, form]);

  if (!selectedStyles) return null;

  const formFontColor = (Form.useWatch("fontColor", form) as string | undefined) ?? selectedStyles.styles.fontColor;
  const formFontFamily =
    (Form.useWatch("fontFamily", form) as FontFamily | undefined) ?? selectedStyles.styles.fontFamily;
  const formFontSize = (Form.useWatch("fontSize", form) as number | undefined) ?? selectedStyles.styles.fontSize;
  const formIsBold = (Form.useWatch("bold", form) as boolean | undefined) ?? selectedStyles.styles.bold;
  const formIsItalic = (Form.useWatch("italic", form) as boolean | undefined) ?? selectedStyles.styles.italic;
  const formHighlightColor =
    (Form.useWatch("highlightColor", form) as string | undefined) ?? selectedStyles.styles.highlightColor;

  const currentBaseline = useMemo(() => {
    return selectedStyles?.styles || null;
  }, [selectedStyles?.styles]);

  const isDirty = useMemo(() => {
    if (!currentBaseline) return false;
    const normColor = (val: string | null | undefined) => {
      if (!val) return "";
      const s = String(val).trim().toLowerCase();
      return s === "transparent" || s === "rgba(255,255,255,0)" ? "" : s;
    };

    const hasStyleTypeDiff = selectedStyleName !== savedStyleName;
    const hasFontFamilyDiff = (formFontFamily || "Arial") !== (currentBaseline.fontFamily || "Arial");
    const hasFontSizeDiff = Number(formFontSize || 10) !== Number(currentBaseline.fontSize || 10);
    const hasFontColorDiff =
      normColor(formFontColor || "#000000") !== normColor(currentBaseline.fontColor || "#000000");
    const hasBoldDiff = Boolean(formIsBold) !== Boolean(currentBaseline.bold);
    const hasItalicDiff = Boolean(formIsItalic) !== Boolean(currentBaseline.italic);
    const hasHighlightDiff = normColor(formHighlightColor) !== normColor(currentBaseline.highlightColor);

    return (
      hasStyleTypeDiff ||
      hasFontFamilyDiff ||
      hasFontSizeDiff ||
      hasFontColorDiff ||
      hasBoldDiff ||
      hasItalicDiff ||
      hasHighlightDiff
    );
  }, [
    selectedStyleName,
    savedStyleName,
    currentBaseline,
    formFontFamily,
    formFontSize,
    formFontColor,
    formIsBold,
    formIsItalic,
    formHighlightColor,
  ]);

  const onSubmit = async () => {
    setIsSaving(true);
    try {
      const values = form.getFieldsValue();
      const newStyles: FontStyleSettings = {
        fontFamily: (values.fontFamily as FontFamily) || selectedStyles.styles.fontFamily || "Arial",
        fontSize: (values.fontSize as number) || selectedStyles.styles.fontSize || 10,
        fontColor: (values.fontColor as string) || selectedStyles.styles.fontColor || "#000000",
        bold: !!values.bold,
        italic: !!values.italic,
        highlightColor: values.highlightColor || null,
      };
      Logger.trace("UserStyleSettings.tsx: Settings changed: User styles saved", {
        action: "settings_change",
        setting: "user_styles",
        styleName: selectedStyles.name,
        style: newStyles,
      });

      if (reportTemplateProjectId) {
        await logAuditAction(
          reportTemplateProjectId,
          "UPDATE",
          `Style Settings: ${selectedStyles.name} - ${JSON.stringify(newStyles)}`
        ).catch((err) => Logger.warn("Audit action logging failed", err));
      }

      await selectedStyles.setStyles(newStyles);
      try {
        localStorage.setItem("jenly_selected_style_name", selectedStyleName);
      } catch (e) {
        // ignore
      }
      setSavedStyleName(selectedStyleName);
      form.setFieldsValue({ ...newStyles });
      message.success(`${selectedStyles.name} style settings saved successfully.`);
    } catch (error) {
      Logger.error("Failed to save style settings", error);
      message.error("Failed to save style settings. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const switchStyleType = (value: string) => {
    Logger.trace("UserStyleSettings.tsx: Style type switched", {
      action: "style_type_switch",
      from: selectedStyles.name,
      to: value,
    });
    setSelectedStyleName(value);
    const target = styles.find((s) => s.name === value)?.styles;
    if (target) {
      form.setFieldsValue({
        fontFamily: target.fontFamily || "Arial",
        fontSize: target.fontSize || 10,
        fontColor: target.fontColor || "#000000",
        bold: !!target.bold,
        italic: !!target.italic,
        highlightColor: target.highlightColor || null,
      });
    }
  };

  return (
    <section className="settings_section">
      <div className="settings_section_header">
        <h3 className="settings_section_title ui-text-section-title">Font style</h3>
        <Select
          className="settings_form_control"
          size="small"
          aria-label="Style type"
          value={selectedStyles.name}
          onChange={switchStyleType}
          options={styles.map((style) => ({ label: style.name, value: style.name }))}
        />
      </div>

      <Form form={form} className="settings_style_form">
        <SettingsRow label="Font color" layout="stacked">
          <Form.Item name="fontColor" noStyle>
            <ColorSwatchPicker ariaLabel="Font color" presets={FONT_COLOR_PRESETS} />
          </Form.Item>
        </SettingsRow>

        <SettingsRow label="Highlight color" layout="stacked">
          <Form.Item name="highlightColor" noStyle>
            <ColorSwatchPicker ariaLabel="Highlight color" presets={HIGHLIGHT_COLOR_PRESETS} allowNone />
          </Form.Item>
        </SettingsRow>

        <SettingsRow label="Font family">
          <Form.Item name="fontFamily" noStyle>
            <Select
              showSearch
              optionFilterProp="label"
              className="settings_form_control"
              options={fontFamilies.map((family) => ({ label: family, value: family }))}
              size="small"
            />
          </Form.Item>
        </SettingsRow>

        <SettingsRow label="Font size">
          <Form.Item name="fontSize" noStyle>
            <InputNumber min={8} max={72} className="settings_form_control_narrow" size="small" />
          </Form.Item>
        </SettingsRow>

        <SettingsRow label="Text style">
          <div className="settings_toggle_group">
            <Form.Item name="bold" noStyle>
              <Tooltip title="Bold">
                <button
                  type="button"
                  className={`settings_toggle_button ${formIsBold ? "active" : ""}`}
                  aria-pressed={!!formIsBold}
                  onClick={() => form.setFieldsValue({ bold: !formIsBold })}
                >
                  <b>B</b>
                </button>
              </Tooltip>
            </Form.Item>
            <Form.Item name="italic" noStyle>
              <Tooltip title="Italic">
                <button
                  type="button"
                  className={`settings_toggle_button ${formIsItalic ? "active" : ""}`}
                  aria-pressed={!!formIsItalic}
                  onClick={() => form.setFieldsValue({ italic: !formIsItalic })}
                >
                  <i>I</i>
                </button>
              </Tooltip>
            </Form.Item>
          </div>
        </SettingsRow>

        <div className="style_settings_preview_wrapper">
          <span
            className="style_settings_preview_text"
            style={{
              color: formFontColor,
              fontFamily: formFontFamily,
              fontSize: `${formFontSize}px`,
              fontWeight: formIsBold ? "bold" : "normal",
              fontStyle: formIsItalic ? "italic" : "normal",
              backgroundColor: formHighlightColor ?? undefined,
            }}
          >
            This is a styled text preview.
          </span>
        </div>

        <div className="settings_save_row">
          <Button type="primary" onClick={onSubmit} loading={isSaving} disabled={!isDirty || isSaving} size="small">
            Save
          </Button>
        </div>
      </Form>
    </section>
  );
}

export default UserStyleSettings;
